import {useWalletConnectionClient} from 'src/v2/state/walletConnection';
import {getStore} from 'src/v2/state/store';
import {syncWalletToSession} from 'src/v2/components/pay/syncWalletToSession';
import {useUpdateSessionOnWalletConnect} from 'src/v2/components/walletConnect/useUpdateSessionOnWalletConnect';
import {getAbiForAsset} from 'src/utils/getAbiForAsset';
import utf8ToHex from 'src/utils/utf8ToHex';
import type {SupportedCurrency} from 'src/v2/state/types';
import type {OpenapiPostV1CryptoInternalRelayQuoteInputInputChainEnum} from '@stripe-internal/data-v1/schema/types';
import {useSessionContext} from 'src/state/usePayinSessionContext';
import {useIntl} from '@sail/intl';
import {handleError} from 'src/v2/components/error/handleError';
import {useResyncSessionState} from 'src/v2/components/pay/useResyncSessionState';
import {decimalAmountToRawAmount} from 'src/utils/helpers';

export const useHandleRelayPayment = () => {
  const intl = useIntl();
  const walletClient = useWalletConnectionClient();
  const store = getStore();
  const [updateSession] = useUpdateSessionOnWalletConnect();
  const {sessionId} = useSessionContext();
  const resyncSessionState = useResyncSessionState();

  const activeWallet = store.useSelector(
    (state) => state.walletConnection.activeWallet,
  );
  const walletSessions = store.useSelector((state) => state.walletSessions);

  const handleRelayPayment = async ({
    selectedCurrency,
    setIsProcessing,
    onConfirmSuccess,
  }: {
    selectedCurrency: SupportedCurrency;
    setIsProcessing: (processing: boolean) => void;
    onConfirmSuccess: () => void | Promise<void>;
  }): Promise<void> => {
    if (!activeWallet) {
      return;
    }

    setIsProcessing(true);

    try {
      await syncWalletToSession(activeWallet, walletSessions, updateSession);

      const {quote} = store.getState().anyCryptoDeposit;
      if (!quote.depositAddress || !quote.inputAmount) {
        throw new Error('Relay quote not available');
      }

      const transferAmount = decimalAmountToRawAmount(
        quote.inputAmount,
        selectedCurrency.currencyMinorUnits,
      );

      store.setValue((state) => {
        state.anyCryptoDeposit.selectedNetwork =
          selectedCurrency.currencyNetwork as OpenapiPostV1CryptoInternalRelayQuoteInputInputChainEnum;
        state.overlay = 'CONFIRM_PAYMENT';
      });

      if (selectedCurrency.assetCode === 'sol') {
        await walletClient.solanaSendSolTransaction({
          destinationAddress: quote.depositAddress,
          amount: transferAmount,
          memoMessage: sessionId,
          walletAddress: activeWallet.address,
        });
      } else if (selectedCurrency.assetCode === 'eth') {
        await walletClient.evmSendNativeTransaction({
          to: quote.depositAddress as `0x${string}`,
          value: transferAmount,
        });
      } else {
        await walletClient.evmSendTransaction({
          abi: getAbiForAsset(selectedCurrency),
          address: selectedCurrency.contractAddress as `0x${string}`,
          functionName: 'transfer',
          args: [quote.depositAddress as `0x${string}`, transferAmount],
          dataSuffix: utf8ToHex(sessionId),
        });
      }

      store.setValue((state) => {
        state.overlay = null;
      });
      await onConfirmSuccess();
    } catch (error) {
      store.setValue((state) => {
        state.overlay = null;
      });
      await resyncSessionState();
      handleError({error, intl, context: 'transfer'});
    } finally {
      setIsProcessing(false);
    }
  };

  return handleRelayPayment;
};
